/**
 * Settings Hook
 *
 * Custom hook for managing plugin settings with API integration.
 *
 * @since 1.0.0
 */

import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AllSettingsData, NotificationData, UseSettingsReturn } from './types';

/**
 * Custom hook for managing plugin settings
 *
 * @since 1.0.0
 *
 * @return Settings management functions and state.
 */
export const useSettings = (): UseSettingsReturn => {
  const defaultSettings = useMemo(
    () => (window.redfoxAddedToCartPopupSettings?.defaults ?? {}) as AllSettingsData,
    []
  );
  const [settings, setSettings] = useState<AllSettingsData>(defaultSettings);
  const [originalSettings, setOriginalSettings] = useState<AllSettingsData>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  /**
   * Add a notification
   *
   * @since 1.0.0
   *
   * @param variant Notification variant.
   * @param message Notification message.
   */
  const addNotification = useCallback((variant: 'success' | 'error', message: string) => {
    const id = Math.random().toString(36).slice(2, 11);
    const notification: NotificationData = { variant, message, id, visible: true };

    setNotifications((prev) => [...prev, notification]);

    // Auto-dismiss is now handled by the NotificationsContainer component
  }, []);

  /**
   * Dismiss a notification
   *
   * @since 1.0.0
   *
   * @param id Notification ID to dismiss.
   */
  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  /**
   * Fetch settings from the API
   *
   * @since 1.0.0
   *
   * @return Promise that resolves when settings are fetched.
   */
  const fetchSettings = useCallback(
    async (noLoading = false): Promise<void> => {
      try {
        setLoading(!noLoading);
        setError(null);

        const response = await apiFetch<AllSettingsData>({
          path: '/redfox-added-to-cart-popup/v1/settings',
          method: 'GET',
        });

        // Merge response with defaults to ensure all required properties exist.
        const apiSettings = {
          ...defaultSettings,
          ...response,
        };

        setSettings(apiSettings);
        setOriginalSettings(apiSettings);
      } catch {
        setError(__('Failed to load settings. Please try again.', 'redfox-added-to-cart-popup'));
      } finally {
        setLoading(false);
      }
    },
    [defaultSettings]
  );

  /**
   * Check if there are unsaved changes
   *
   * @since 1.0.0
   */
  const hasChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  }, [settings, originalSettings]);

  /**
   * Update settings locally (no auto-save)
   *
   * @since 1.0.0
   *
   * @param section Section to update.
   * @param newSettings New settings for the section.
   */
  const updateSettings = useCallback(
    (section: keyof AllSettingsData, newSettings: AllSettingsData[keyof AllSettingsData]) => {
      setSettings((prevSettings) => ({
        ...prevSettings,
        [section]: newSettings,
      }));
    },
    []
  );

  /**
   * Save settings to the API
   *
   * @since 1.0.0
   *
   * @return Promise that resolves when settings are saved.
   */
  const saveSettings = useCallback(async (): Promise<void> => {
    try {
      setIsSaving(true);
      setError(null);

      await apiFetch({
        path: '/redfox-added-to-cart-popup/v1/settings',
        method: 'POST',
        data: {
          settings,
        },
      });

      // Update original settings after successful save.
      setOriginalSettings(settings);

      // Add success notification
      addNotification('success', __('Settings saved successfully!', 'redfox-added-to-cart-popup'));
    } catch {
      const errorMessage = __(
        'Failed to save settings. Please try again.',
        'redfox-added-to-cart-popup'
      );
      setError(errorMessage);
      addNotification('error', errorMessage);
      throw new Error('Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [settings, addNotification]);

  /**
   * Reset settings to original values
   *
   * @since 1.0.0
   */
  const resetSettings = useCallback(() => {
    setSettings(originalSettings);
  }, [originalSettings]);

  /**
   * Reset settings to defaults via API
   *
   * @since 1.0.0
   *
   * @return Promise that resolves when settings are reset to defaults.
   */
  const resetToDefaults = useCallback(async (): Promise<void> => {
    try {
      setIsResetting(true);
      setError(null);

      await apiFetch({
        path: '/redfox-added-to-cart-popup/v1/settings',
        method: 'DELETE',
      });

      // Refresh settings from API to get the defaults.
      await fetchSettings(true);

      // Add success notification
      addNotification(
        'success',
        __('Settings reset to defaults successfully!', 'redfox-added-to-cart-popup')
      );
    } catch {
      const errorMessage = __(
        'Failed to reset settings to defaults. Please try again.',
        'redfox-added-to-cart-popup'
      );
      setError(errorMessage);
      addNotification('error', errorMessage);
      throw new Error('Reset to defaults failed');
    } finally {
      setIsResetting(false);
    }
  }, [fetchSettings, addNotification]);

  /**
   * Refresh settings from the API
   *
   * @since 1.0.0
   *
   * @return Promise that resolves when settings are refreshed.
   */
  const refreshSettings = useCallback(async (): Promise<void> => {
    await fetchSettings();
  }, [fetchSettings]);

  // Load settings on mount.
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    originalSettings,
    loading,
    isSaving,
    isResetting,
    error,
    notifications,
    hasChanges,
    updateSettings,
    saveSettings,
    resetSettings,
    resetToDefaults,
    refreshSettings,
    dismissNotification,
  };
};
